/**
 * A vector layer read from its GeoPackage in the browser, through a signed bucket link.
 * Whole-file: a tree inventory is at most hundreds of MB. Above VECTOR_MAX_BYTES the layer is
 * refused with `too_large` rather than downloaded; below it the download is streamed so the
 * caller can show progress. A table in a projected system the viewer
 * knows is reprojected to WGS84; any other system is refused with `unsupported_crs`.
 */
import { gpkgCache, type GpkgCache } from './gpkgCache';
import type { Threshold } from '@/features/reports/trees/config';
import { buildCrownLayer, type CrownLayer } from './crownLayer';
import type { GpkgErrorCode } from './gpkg';
import { parseGpkgLayer as parseInThread, parseGpkgTables as parseTablesInThread, type Layer } from './gpkgParse';
import type { LinkSource } from './linkSource';
import { tileWanted } from './tileMask';

export const VECTOR_MAX_MB = 500;
export const VECTOR_MAX_BYTES = VECTOR_MAX_MB * 1024 * 1024;

/** Where a layer load is: bytes arriving, or one step of the parse (`features` counts rows). */
export type ReadStep = 'open' | 'rows' | 'features' | 'index';
export type VectorProgress = { phase: 'downloading'; done: number; total: number } | { phase: 'reading'; step: ReadStep; done?: number; total?: number };
export type ProgressFn = (progress: VectorProgress) => void;

export type VectorSourceErrorCode = 'fetch' | 'too_large' | 'unsupported_crs' | GpkgErrorCode;

export type WorkerRequest =
  | { kind: 'parse'; bytes: Uint8Array; table?: string; crowns?: { thresholds: Threshold[] } }
  | { kind: 'tables'; bytes: Uint8Array; tables: string[]; simplifyM?: Partial<Record<string, number>> }
  | { kind: 'tile'; id: number; z: number; x: number; y: number }
  | { kind: 'retag'; id: number; thresholds: Threshold[] };
export type WorkerResponse =
  | { kind: 'progress'; progress: VectorProgress }
  | { kind: 'features'; fc: Layer }
  | { kind: 'tables'; layers: Record<string, Layer> }
  | { kind: 'crowns'; points: Layer; hullHa: number }
  | { kind: 'normalized'; bytes: Uint8Array | null }
  | { kind: 'error'; code: VectorSourceErrorCode; message: string }
  | { kind: 'tile'; id: number; bytes: Uint8Array }
  | { kind: 'retagged'; id: number };

/** A crown layer's polygons, wherever they live, answering the map's tile requests. */
export interface CrownServer {
  tile(z: number, x: number, y: number): Promise<Uint8Array>;
  retag(thresholds: Threshold[]): Promise<void>;
  close(): void;
}

export class VectorSourceError extends Error {
  constructor(
    public readonly code: VectorSourceErrorCode,
    message?: string,
    public readonly size?: number,
  ) {
    super(message ?? code);
    this.name = 'VectorSourceError';
  }
}

export interface LoadOptions {
  /** A feature table to read; the first one in `gpkg_contents` otherwise. */
  table?: string;
  fetchImpl?: typeof fetch;
  onProgress?: ProgressFn;
}

async function fetchGpkgBytes(url: string, fetchImpl: typeof fetch = fetch, onProgress?: ProgressFn): Promise<Uint8Array> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new VectorSourceError('fetch', `bucket ${response.status}`);
  const declared = Number(response.headers.get('content-length') ?? 0);
  if (declared > VECTOR_MAX_BYTES) throw new VectorSourceError('too_large', undefined, declared);
  onProgress?.({ phase: 'downloading', done: 0, total: declared });
  const reader = response.body?.getReader();
  if (!reader) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.length > VECTOR_MAX_BYTES) throw new VectorSourceError('too_large', undefined, bytes.length);
    onProgress?.({ phase: 'downloading', done: bytes.length, total: bytes.length });
    return bytes;
  }
  const chunks: Uint8Array[] = [];
  let done = 0;
  for (;;) {
    const { value, done: end } = await reader.read();
    if (end) break;
    chunks.push(value);
    done += value.length;
    if (done > VECTOR_MAX_BYTES) {
      await reader.cancel();
      throw new VectorSourceError('too_large', undefined, done);
    }
    onProgress?.({ phase: 'downloading', done, total: Math.max(declared, done) });
  }
  const bytes = new Uint8Array(done);
  let offset = 0;
  for (const c of chunks) {
    bytes.set(c, offset);
    offset += c.length;
  }
  return bytes;
}

export async function loadGpkgLayer(url: string, options: LoadOptions = {}): Promise<Layer> {
  const bytes = await fetchGpkgBytes(url, options.fetchImpl, options.onProgress);
  const { fc, normalized } = await parseGpkgLayer(bytes, options.table, options.onProgress);
  void normalized.catch(() => undefined);
  return fc;
}

/**
 * Several feature tables of one GeoPackage from one download and one parse; a table that is
 * not there (or cannot be drawn) is left out of the result, so the caller decides which ones it
 * cannot do without. `simplifyM` simplifies a table's polygons as they are read (gpkgParse.ts).
 */
export async function loadGpkgLayers(url: string, tables: string[], options: Omit<LoadOptions, 'table'> & { simplifyM?: Partial<Record<string, number>> } = {}): Promise<Partial<Record<string, Layer>>> {
  const bytes = await fetchGpkgBytes(url, options.fetchImpl, options.onProgress);
  return parseTables(bytes, tables, options.simplifyM, options.onProgress);
}

export interface LinkedLayer {
  object: string;
  path: string;
  /** The object's listing digest; the cache is keyed by it. */
  digest: string;
}

/** Parsed layers of this session by object, path and digest; a handful, since each may be very large. */
const LAYER_MEMORY_MAX = 4;
const layerMemory = new Map<string, Promise<Layer>>();
/** Crown layers keep a worker holding every polygon, so fewer are kept. */
const CROWN_MEMORY_MAX = 2;
const crownMemory = new Map<string, Promise<LoadedCrowns>>();
/** The crown server the map's tile requests for an object go to, or the load that will provide it (null once it failed). */
const crownServers = new Map<string, Promise<CrownServer | null>>();

export function forgetLayers(): void {
  layerMemory.clear();
  tablesMemory.clear();
  for (const held of crownMemory.values()) void held.then((c) => c.server.close(), () => undefined);
  crownMemory.clear();
  crownServers.clear();
}

export interface LinkedLoadOptions {
  cache?: GpkgCache;
  fetchImpl?: typeof fetch;
  onProgress?: ProgressFn;
  /** Resolves once the reprojected copy (if any) is in the cache; for tests and diagnostics. */
  onNormalized?: (written: boolean) => void;
}

export interface LoadedCrowns {
  /** One point per crown with the properties the page needs; the polygons stay with the server. */
  points: Layer;
  hullHa: number;
  server: CrownServer;
}

const memoryKey = (layer: LinkedLayer) => `${layer.object}\u0000${layer.path}\u0000${layer.digest}`;

function remember<T>(memory: Map<string, Promise<T>>, max: number, key: string, load: () => Promise<T>, evict?: (held: Promise<T>) => void): Promise<T> {
  const held = memory.get(key);
  if (held) {
    memory.delete(key);
    memory.set(key, held); // most recent last
    return held;
  }
  const loading = load().catch((e: unknown) => {
    memory.delete(key);
    throw e;
  });
  memory.set(key, loading);
  while (memory.size > max) {
    const oldest = memory.keys().next().value!;
    evict?.(memory.get(oldest)!);
    memory.delete(oldest);
  }
  return loading;
}

/**
 * A layer, from memory when this session already parsed it; from the browser's GeoPackage
 * cache when its digest is there; otherwise signed, downloaded, cached and read. A table
 * that had to be reprojected is written back to the cache in EPSG:4326 once the features are
 * out, so the next read skips that step. A file that cannot be read is dropped from the cache.
 */
export function loadLinkedGpkgLayer(layer: LinkedLayer, links: LinkSource, options: LinkedLoadOptions = {}): Promise<Layer> {
  return remember(layerMemory, LAYER_MEMORY_MAX, memoryKey(layer), async () => {
    const parsed = await loadLinkedBytes(layer, links, options, (bytes) => parseGpkgLayer(bytes, undefined, options.onProgress));
    return parsed.fc;
  });
}

const TABLES_MEMORY_MAX = 4;
const tablesMemory = new Map<string, Promise<Partial<Record<string, Layer>>>>();

/**
 * Several tables of one linked GeoPackage, the way `loadLinkedGpkgLayer` reads one: from this
 * session's memory, else from the browser's GeoPackage cache by digest, else signed and
 * downloaded (and cached). All the tables come from one parse over one copy of the bytes. The
 * cache holds the file as it came; a simplified read is never written back.
 */
export function loadLinkedGpkgLayers(layer: LinkedLayer, links: LinkSource, tables: string[], options: LinkedLoadOptions & { simplifyM?: Partial<Record<string, number>> } = {}): Promise<Partial<Record<string, Layer>>> {
  const key = `${memoryKey(layer)}\u0000${tables.join(',')}\u0000${JSON.stringify(options.simplifyM ?? {})}`;
  return remember(tablesMemory, TABLES_MEMORY_MAX, key, async () => {
    const parsed = await loadLinkedBytes(layer, links, options, async (bytes) => ({ layers: await parseTables(bytes, tables, options.simplifyM, options.onProgress), normalized: Promise.resolve(null) }));
    return parsed.layers;
  });
}

/**
 * A crown layer: the same path, but the polygons stay in the worker as a tile index that the
 * `gpkg://` protocol serves, and the page receives centroid points and the hull area.
 */
export function loadLinkedCrownLayer(layer: LinkedLayer, links: LinkSource, options: LinkedLoadOptions & { thresholds?: Threshold[] } = {}): Promise<LoadedCrowns> {
  const loading = remember(
    crownMemory,
    CROWN_MEMORY_MAX,
    memoryKey(layer),
    async () => {
      const parsed = await loadLinkedBytes(layer, links, options, (bytes) => parseCrowns(bytes, options.thresholds ?? [], options.onProgress));
      return parsed.crowns;
    },
    (held) => void held.then((c) => c.server.close(), () => undefined),
  );
  // The load's failure is the caller's to handle; the tile side only needs to know there is no server.
  crownServers.set(
    layer.object,
    loading.then(
      (c) => c.server,
      () => null,
    ),
  );
  return loading;
}

/** The tile for a `gpkg://<object>/<z>/<x>/<y>` url; an empty tile for an object nobody loaded. */
export async function serveGpkgTile(url: string): Promise<Uint8Array> {
  const m = /^gpkg:\/\/([^/]+)\/(\d+)\/(\d+)\/(\d+)/.exec(url);
  const pending = m && crownServers.get(m[1]!);
  if (!m || !pending) return new Uint8Array(0);
  const [z, x, y] = [Number(m[2]), Number(m[3]), Number(m[4])];
  // A tile outside the object's mask is empty without asking the worker (tileMask.ts).
  if (!tileWanted(m[1]!, z, x, y)) return new Uint8Array(0);
  const server = await pending;
  return server ? server.tile(z, x, y) : new Uint8Array(0);
}

interface ParsedBytes {
  normalized: Promise<Uint8Array | null>;
}

async function loadLinkedBytes<T extends ParsedBytes>(layer: LinkedLayer, links: LinkSource, options: LinkedLoadOptions, parse: (bytes: Uint8Array) => Promise<T>): Promise<T> {
  const cache = options.cache ?? gpkgCache;
  const cached = await cache.get(layer.object, layer.path, layer.digest);
  let bytes = cached;
  if (!bytes) {
    bytes = await fetchGpkgBytes(await links.url(layer.object, layer.path), options.fetchImpl, options.onProgress);
  }
  if (!cached) await cache.put(layer.object, layer.path, layer.digest, bytes);
  let parsed: T;
  try {
    parsed = await parse(bytes);
  } catch (e) {
    await cache.remove(layer.object, layer.path, layer.digest);
    throw e;
  }
  void parsed.normalized
    .then(async (normalized) => {
      if (normalized) await cache.put(layer.object, layer.path, layer.digest, normalized);
      options.onNormalized?.(normalized !== null);
    })
    .catch(() => options.onNormalized?.(false));
  return parsed;
}

/** The features now, and later the file rewritten in EPSG:4326 when a reprojection happened. */
interface ParsedLayer {
  fc: Layer;
  normalized: Promise<Uint8Array | null>;
}
interface ParsedCrowns {
  crowns: LoadedCrowns;
  normalized: Promise<Uint8Array | null>;
}

const asError = (e: unknown): VectorSourceError => {
  if (e instanceof VectorSourceError) return e;
  if (e instanceof Error && 'code' in e) return new VectorSourceError(e.code as VectorSourceErrorCode, e.message);
  return new VectorSourceError('fetch', String(e));
};

/** In the thread (no `Worker`: tests), the crown layer itself answers. */
function localServer(crowns: CrownLayer): CrownServer {
  return {
    tile: async (z, x, y) => crowns.tile(z, x, y),
    retag: async (thresholds) => crowns.retag(thresholds),
    close: () => undefined,
  };
}

/**
 * Parse off the main thread: sql.js copies the whole file into its heap and a large table
 * takes seconds to walk, which would freeze the page. Where there is no `Worker` (tests),
 * the same parser runs in the thread.
 */
async function parseGpkgLayer(bytes: Uint8Array, table?: string, onProgress?: ProgressFn): Promise<ParsedLayer> {
  if (typeof Worker === 'undefined') {
    try {
      const { fc, normalized } = await parseInThread(bytes, table, undefined, onProgress);
      return { fc, normalized: Promise.resolve(normalized) };
    } catch (e) {
      throw asError(e);
    }
  }
  const run = startWorker({ kind: 'parse', bytes, table }, onProgress);
  const fc = await run.features;
  void run.normalized.finally(() => run.close());
  return { fc, normalized: run.normalized };
}

/** Several tables in one worker run over one copy of the bytes; in the thread where there is no `Worker`. */
async function parseTables(bytes: Uint8Array, tables: string[], simplifyM: Partial<Record<string, number>> = {}, onProgress?: ProgressFn): Promise<Partial<Record<string, Layer>>> {
  if (typeof Worker === 'undefined') {
    try {
      return await parseTablesInThread(bytes, tables, onProgress, simplifyM);
    } catch (e) {
      throw asError(e);
    }
  }
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./gpkg.worker.ts', import.meta.url), { type: 'module' });
    const done = () => worker.terminate();
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const r = e.data;
      if (r.kind === 'progress') onProgress?.(r.progress);
      else if (r.kind === 'tables') {
        done();
        resolve(r.layers);
      } else if (r.kind === 'error') {
        done();
        reject(new VectorSourceError(r.code, r.message));
      }
    };
    worker.onerror = (e) => {
      done();
      reject(new VectorSourceError('not_gpkg', e.message));
    };
    const request: WorkerRequest = { kind: 'tables', bytes, tables, simplifyM };
    worker.postMessage(request, [bytes.buffer]);
  });
}

async function parseCrowns(bytes: Uint8Array, thresholds: Threshold[], onProgress?: ProgressFn): Promise<ParsedCrowns> {
  if (typeof Worker === 'undefined') {
    try {
      const { fc, normalized } = await parseInThread(bytes, undefined, undefined, onProgress);
      onProgress?.({ phase: 'reading', step: 'index' });
      const crowns = buildCrownLayer(fc, thresholds);
      return { crowns: { points: crowns.points, hullHa: crowns.hullHa, server: localServer(crowns) }, normalized: Promise.resolve(normalized) };
    } catch (e) {
      throw asError(e);
    }
  }
  const run = startWorker({ kind: 'parse', bytes, crowns: { thresholds } }, onProgress);
  const { points, hullHa } = await run.crowns;
  return { crowns: { points, hullHa, server: run }, normalized: run.normalized };
}

interface WorkerRun extends CrownServer {
  features: Promise<Layer>;
  crowns: Promise<{ points: Layer; hullHa: number }>;
  normalized: Promise<Uint8Array | null>;
}

/** Posts a parse to a fresh worker and exposes what comes back; `close` ends the worker. */
function startWorker(request: Extract<WorkerRequest, { kind: 'parse' }>, onProgress?: ProgressFn): WorkerRun {
  const worker = new Worker(new URL('./gpkg.worker.ts', import.meta.url), { type: 'module' });
  let settle: { features: [(v: Layer) => void, (e: Error) => void]; crowns: [(v: { points: Layer; hullHa: number }) => void, (e: Error) => void]; normalized: (v: Uint8Array | null) => void };
  const features = new Promise<Layer>((res, rej) => (settle = { ...settle!, features: [res, rej] }));
  const crowns = new Promise<{ points: Layer; hullHa: number }>((res, rej) => (settle = { ...settle!, crowns: [res, rej] }));
  const normalized = new Promise<Uint8Array | null>((res) => (settle = { ...settle!, normalized: res }));
  void features.catch(() => undefined);
  void crowns.catch(() => undefined);
  const pending = new Map<number, (r: WorkerResponse) => void>();
  let seq = 0;
  const fail = (e: VectorSourceError) => {
    settle.features[1](e);
    settle.crowns[1](e);
    settle.normalized(null);
    for (const resolve of pending.values()) resolve({ kind: 'error', code: e.code, message: e.message });
    pending.clear();
  };
  worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
    const r = e.data;
    if ('id' in r) {
      pending.get(r.id)?.(r);
      pending.delete(r.id);
    } else if (r.kind === 'progress') onProgress?.(r.progress);
    else if (r.kind === 'features') settle.features[0](r.fc);
    else if (r.kind === 'crowns') settle.crowns[0]({ points: r.points, hullHa: r.hullHa });
    else if (r.kind === 'normalized') settle.normalized(r.bytes);
    else if (r.kind === 'error') fail(new VectorSourceError(r.code, r.message));
  };
  worker.onerror = (e) => fail(new VectorSourceError('not_gpkg', e.message));
  const ask = (make: (id: number) => WorkerRequest) =>
    new Promise<WorkerResponse>((resolve) => {
      const id = ++seq;
      pending.set(id, resolve);
      worker.postMessage(make(id));
    });
  // The buffer is handed over, not copied; the cache write happened before this call.
  worker.postMessage(request, [request.bytes.buffer]);
  return {
    features,
    crowns,
    normalized,
    async tile(z, x, y) {
      const r = await ask((id) => ({ kind: 'tile', id, z, x, y }));
      return r.kind === 'tile' ? r.bytes : new Uint8Array(0);
    },
    async retag(thresholds) {
      await ask((id) => ({ kind: 'retag', id, thresholds }));
    },
    close: () => worker.terminate(),
  };
}
